import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@shared/database.types";

export type ReferralPartner = Database["public"]["Tables"]["referral_partners"]["Row"];
type PartnerInsert = Database["public"]["Tables"]["referral_partners"]["Insert"];
type PartnerUpdate = Database["public"]["Tables"]["referral_partners"]["Update"];

export type ReferralCommission =
  Database["public"]["Tables"]["referral_commissions"]["Row"];
type CommissionInsert =
  Database["public"]["Tables"]["referral_commissions"]["Insert"];
type CommissionUpdate =
  Database["public"]["Tables"]["referral_commissions"]["Update"];

export type CommissionWithRefs = ReferralCommission & {
  partner?: { name: string } | null;
  client?: { name: string } | null;
  project?: { name: string } | null;
};

// movement_id è gestito dal trigger DB sync_commission_movement: la UI non lo tocca mai.
export type CommissionInsertInput = Omit<CommissionInsert, "agency_id" | "movement_id">;
export type CommissionUpdateInput = Omit<CommissionUpdate, "agency_id" | "movement_id">;

// ---------------------------------------------------------------------------
// Referenti (partners)
// ---------------------------------------------------------------------------

export function useReferralPartners() {
  const { agencyId } = useAuth();

  return useQuery({
    queryKey: ["referral-partners", agencyId],
    enabled: !!agencyId,
    queryFn: async (): Promise<ReferralPartner[]> => {
      const { data, error } = await supabase
        .from("referral_partners")
        .select("*")
        .eq("agency_id", agencyId!)
        .order("active", { ascending: false })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ReferralPartner[];
    },
  });
}

export function useCreatePartner() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();
  return useMutation({
    mutationFn: async (input: Omit<PartnerInsert, "agency_id">) => {
      if (!agencyId) throw new Error("agency_id mancante");
      const { data, error } = await supabase
        .from("referral_partners")
        .insert({ ...input, agency_id: agencyId })
        .select()
        .single();
      if (error) throw error;
      return data as ReferralPartner;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["referral-partners"] });
    },
  });
}

export function useUpdatePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: PartnerUpdate }) => {
      const { data, error } = await supabase
        .from("referral_partners")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as ReferralPartner;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["referral-partners"] });
      // Il nome del referente compare nel join delle commissioni
      qc.invalidateQueries({ queryKey: ["commissions"] });
    },
  });
}

export function useDeletePartner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("referral_partners")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["referral-partners"] });
      qc.invalidateQueries({ queryKey: ["commissions"] });
      qc.invalidateQueries({ queryKey: ["referral-counts"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Commissioni
// ---------------------------------------------------------------------------

export function useCommissions(partnerId?: string | null) {
  const { agencyId } = useAuth();

  return useQuery({
    queryKey: ["commissions", agencyId, partnerId ?? null],
    enabled: !!agencyId,
    queryFn: async (): Promise<CommissionWithRefs[]> => {
      let q = supabase
        .from("referral_commissions")
        .select(
          "*, partner:referral_partners(name), client:clients(name), project:projects(name)",
        )
        .eq("agency_id", agencyId!)
        .order("matured_on", { ascending: false })
        .order("created_at", { ascending: false });
      if (partnerId) q = q.eq("partner_id", partnerId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as CommissionWithRefs[];
    },
  });
}

export function useCreateCommission() {
  const qc = useQueryClient();
  const { agencyId } = useAuth();
  return useMutation({
    mutationFn: async (input: CommissionInsertInput) => {
      if (!agencyId) throw new Error("agency_id mancante");
      const { data, error } = await supabase
        .from("referral_commissions")
        .insert({ ...input, agency_id: agencyId })
        .select()
        .single();
      if (error) throw error;
      return data as ReferralCommission;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commissions"] });
      // Il trigger DB può creare il movimento in Finanze
      qc.invalidateQueries({ queryKey: ["movements"] });
    },
  });
}

export function useUpdateCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: CommissionUpdateInput;
    }) => {
      const { data, error } = await supabase
        .from("referral_commissions")
        .update(patch)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as ReferralCommission;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commissions"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
    },
  });
}

export function useDeleteCommission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("referral_commissions")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commissions"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Conteggi clienti/progetti portati da ogni referente (query leggera)
// ---------------------------------------------------------------------------

export interface ReferralCounts {
  clients: number;
  projects: number;
}

export function useReferralCounts() {
  const { agencyId } = useAuth();

  return useQuery({
    queryKey: ["referral-counts", agencyId],
    enabled: !!agencyId,
    queryFn: async (): Promise<Record<string, ReferralCounts>> => {
      const [clientsRes, projectsRes] = await Promise.all([
        supabase
          .from("clients")
          .select("referral_partner_id")
          .eq("agency_id", agencyId!)
          .not("referral_partner_id", "is", null),
        supabase
          .from("projects")
          .select("referral_partner_id")
          .eq("agency_id", agencyId!)
          .not("referral_partner_id", "is", null),
      ]);
      if (clientsRes.error) throw clientsRes.error;
      if (projectsRes.error) throw projectsRes.error;

      const counts: Record<string, ReferralCounts> = {};
      const bump = (id: string | null, key: keyof ReferralCounts) => {
        if (!id) return;
        counts[id] = counts[id] ?? { clients: 0, projects: 0 };
        counts[id][key] += 1;
      };
      for (const row of clientsRes.data ?? []) bump(row.referral_partner_id, "clients");
      for (const row of projectsRes.data ?? []) bump(row.referral_partner_id, "projects");
      return counts;
    },
  });
}
